import { useCallback, useEffect, useRef, useState } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import { useLocationSearch } from 'src/app/pages/search/shared/hooks';
import type { LocationItem, LocationItemTypeEnum } from 'src/app/pages/search/shared/types';

type Result = ModelHookResult<
	{
		data: {
			isLoading: boolean;
			isError: boolean;
			locations: readonly LocationItem[];
		};
		input: {
			inputRef: React.RefObject<HTMLInputElement | null>;
			searchTerm: string;
		};
	},
	{
		handleInputFocus: () => void;
		handleInputChange: (value: string) => void;
		clearSearch: () => void;
		selectLocation: (value: string, type: LocationItemTypeEnum) => void;
	}
>;

export function useLocationFilter(): Result {
	const inputRef = useRef<HTMLInputElement>(null);
	const track = useTracking();

	const {
		data: { locations, isLoading, isError, selectedLocation },
		actions: { searchDebounced, onResetQuery, selectLocation },
	} = useLocationSearch();

	const [searchTerm, setSearchTerm] = useState(selectedLocation || '');

	useEffect(() => {
		if (!selectedLocation && searchTerm) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setSearchTerm('');
		}
		if (selectedLocation && selectedLocation !== searchTerm) {
			setSearchTerm(selectedLocation);
		}
		// This is used to sync the location state with the input value
		// TODO: remove this once the autocomplete component is implemented in design system
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedLocation]);

	return {
		data: {
			data: {
				isLoading,
				isError,
				locations,
			},
			input: {
				searchTerm,
				inputRef,
			},
		},
		actions: {
			handleInputFocus: useCallback(() => {
				void track('search_location_filter_enter');
			}, [track]),
			handleInputChange: useCallback(
				(value: string) => {
					void track('search_location_filter_search');
					searchDebounced(value);
					setSearchTerm(value);
				},
				[searchDebounced, track],
			),
			clearSearch: useCallback(() => {
				onResetQuery();
				setSearchTerm('');
			}, [onResetQuery]),
			selectLocation,
		},
	};
}
